import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Form, Select, message, Dropdown, InputNumber } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import type { Market } from "@/shared/types/campaign/types";
import { tableCardStyle, headerCellStyle, cellStyle, footerStyle, addLinkStyle } from "./styles";
import { countryOptions, getCountryShort } from "@/shared/utils/formatters";
import { Icon } from "@/shared/components/Icon/Icon";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal";

// Buffer-aware behavior: Add, Edit, and Delete are all deferred until the
// user clicks "Save Changes" in the parent action bar. The parent owns the
// authoritative markets/blocks state and the pending-ops queue; this tab
// performs an optimistic local insert/patch/remove for snappy feedback while
// the markets prop catches up on the next render.

const StyledDropdownMenu = styled.div`
	width: 170px;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0px 4px 12px 0px rgba(21, 89, 165, 0.23);
	overflow: hidden;
`;

const StyledDropdownItem = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 18px;
	border: none;
	background: #ffffff;
	color: #35373d;
	font-size: 14px;
	text-align: left;
	cursor: pointer;
	opacity: 1;
	transition: background 0.2s;

	&:not(:last-child) {
		margin-bottom: 1px;
	}
	&:hover {
		background: #f7f8fc;
	}
`;

interface Props {
	markets: Market[];
	onAdd: (country: string, numCreators?: number, numAssets?: number) => Promise<void>;
	onRemove: (country: string) => void;
	onUpdate?: (market: Market) => Promise<void>;
	readOnly?: boolean;
}

export const MarketsTab: React.FC<Props> = ({ markets, onAdd, onRemove, onUpdate, readOnly = false }) => {
	// Add Market State
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [addForm] = Form.useForm();
	const [addFormValid, setAddFormValid] = useState(false);
	const [adding, setAdding] = useState(false);

	// Edit Market State
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editForm] = Form.useForm();
	const [editingMarket, setEditingMarket] = useState<Market | null>(null);
	const [saving, setSaving] = useState(false);

	const [localMarkets, setLocalMarkets] = useState<Market[]>(markets);

	// Sync local state when props change
	useEffect(() => {
		setLocalMarkets(markets);
	}, [markets]);

	// ── Add Methods ──

	const handleAddSubmit = async () => {
		try {
			const values = await addForm.validateFields();
			setAdding(true);
			const optimisticMarket: Market = {
				key: Date.now(),
				country: values.country,
				countryShort: getCountryShort(values.country),
				creators: values.creators || 0,
				videos: values.assets || 0,
			};
			// Optimistic local insert for instant feedback. The parent will
			// also patch its `markets` state and the prop sync useEffect will
			// reconcile this row on the next render.
			setLocalMarkets((prev) => [...prev, optimisticMarket]);
			setAddModalOpen(false);
			addForm.resetFields();
			setAddFormValid(false);

			// Hand off to the parent which enqueues the buffered add_market op.
			// This is non-throwing (no backend call) so no inner try/catch.
			await onAdd(values.country, values.creators || 0, values.assets || 0);
		} catch (e: any) {
			if (e?.errorFields) return; // form validation error, already shown inline
			console.error(e);
			message.error("Failed to add market");
		} finally {
			setAdding(false);
		}
	};

	const closeAddModal = () => {
		setAddModalOpen(false);
		addForm.resetFields();
		setAddFormValid(false);
	};

	// ── Edit Methods ──

	const openEditModal = (market: Market) => {
		setEditingMarket(market);
		editForm.setFieldsValue({
			creators: market.creators,
			videos: market.videos,
		});
		setEditModalOpen(true);
	};

	const closeEditModal = () => {
		setEditModalOpen(false);
		setEditingMarket(null);
		editForm.resetFields();
	};

	const handleEditSubmit = async () => {
		if (!editingMarket) return;
		try {
			const values = await editForm.validateFields();
			setSaving(true);
			const updated: Market = {
				...editingMarket,
				creators: values.creators,
				videos: values.videos,
			};
			// Buffered: optimistic local patch + parent enqueues. The parent's
			// markets state will also flow back through the markets prop on
			// the next render, but patching here keeps the table responsive
			// even within the same tick.
			setLocalMarkets((prev) => prev.map((item) => (item.key === editingMarket.key ? updated : item)));
			closeEditModal();
			if (onUpdate) {
				await onUpdate(updated);
			}
		} catch (e) {
			console.error(e);
			message.error("Failed to save market");
		} finally {
			setSaving(false);
		}
	};

	// ── Delete Methods ──
	// No more destructive confirm modal — the buffered delete is itself
	// reversible via the "Discard and Close" path in the unsaved-changes
	// confirm modal.

	const handleDelete = (market: Market) => {
		if (!market.country) return;
		setLocalMarkets((prev) => prev.filter((m) => m.key !== market.key));
		onRemove(market.country);
	};

	return (
		<>
			<div style={tableCardStyle}>
				<table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead>
						<tr>
							<th style={headerCellStyle} align="left">
								Market
							</th>
							<th style={headerCellStyle} align="center">
								# of Creators
							</th>
							<th style={headerCellStyle} align="center">
								# of Assets
							</th>
							{!readOnly && <th style={{ ...headerCellStyle, width: 48 }} />}
						</tr>
					</thead>
					<tbody>
						{localMarkets.map((m) => (
							<tr key={m.key}>
								<td style={cellStyle}>
									{m.country} ({m.countryShort})
								</td>
								<td style={{ ...cellStyle, width: 160, textAlign: "center" }}>{m.creators ?? "--"}</td>
								<td style={{ ...cellStyle, width: 160, textAlign: "center" }}>{m.videos ?? "--"}</td>

								{!readOnly && (
									<td style={{ ...cellStyle, width: 200, textAlign: "center" }}>
										<Dropdown
											trigger={["click"]}
											placement={"right" as any}
											getPopupContainer={() => document.body}
											overlayStyle={{ position: "fixed" }}
											dropdownRender={() => (
												<StyledDropdownMenu>
													<StyledDropdownItem onClick={() => openEditModal(m)}>
														<Icon src="/icon/pencil-01.svg" size={18} />
														Edit
													</StyledDropdownItem>
													<StyledDropdownItem onClick={() => handleDelete(m)}>
														<Icon src="/icon/trash.svg" size={18} color="#D30024" />
														<span style={{ color: "#D30024" }}>Delete</span>
													</StyledDropdownItem>
												</StyledDropdownMenu>
											)}
										>
											<MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
										</Dropdown>
									</td>
								)}
							</tr>
						))}
					</tbody>
				</table>

				{!readOnly && (
					<div style={footerStyle}>
						<button style={addLinkStyle} onClick={() => setAddModalOpen(true)}>
							<PlusOutlined /> Add a Market
						</button>
					</div>
				)}
			</div>

			{/* Add Market Modal */}
			<DynamicModal
				open={addModalOpen}
				onCancel={closeAddModal}
				title="Add a Market"
				icon={<PlusOutlined style={{ fontSize: 24 }} />}
				description="Select a country to add as a new market for this campaign."
				confirmText="Submit"
				onConfirm={handleAddSubmit}
				confirmDisabled={!addFormValid}
				loading={adding}
				cancelText="Cancel"
			>
				<Form
					form={addForm}
					layout="vertical"
					initialValues={{ creators: 0, assets: 0 }}
					onValuesChange={() => setAddFormValid(!!addForm.getFieldValue("country"))}
				>
					<Form.Item
						name="country"
						label="Country"
						rules={[{ required: true, message: "Please select a country!" }]}
					>
						<Select
							options={countryOptions
								.filter(
									(c) =>
										!localMarkets.some((m) => m.country === c.value || m.countryShort === c.value),
								)
								.map((c) => ({ label: c.label, value: c.value }))}
							placeholder="Search or select a country"
							showSearch
							filterOption={(input, option) =>
								((option?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
							}
							getPopupContainer={(trigger) => trigger.parentElement || document.body}
							style={{ width: "100%" }}
						/>
					</Form.Item>

					<Form.Item
						name="creators"
						label="Number of Creators"
						rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
					>
						<InputNumber min={0} placeholder="Enter number of creators" />
					</Form.Item>

					<Form.Item
						name="assets"
						label="Number of Assets"
						rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
					>
						<InputNumber min={0} placeholder="Enter number of assets" />
					</Form.Item>
				</Form>
			</DynamicModal>

			{/* Edit Market Modal */}
			<DynamicModal
				open={editModalOpen}
				onCancel={closeEditModal}
				title={`Edit ${editingMarket ? `${editingMarket.country} (${editingMarket.countryShort})` : ""}`}
				icon={{ src: "/icon/pencil-01.svg", size: 24 }}
				confirmText="Save"
				onConfirm={handleEditSubmit}
				loading={saving}
				cancelText="Cancel"
			>
				<Form form={editForm} layout="vertical">
					<Form.Item
						name="creators"
						label="Number of Creators"
						rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
					>
						<InputNumber min={0} placeholder="Enter number of creators" />
					</Form.Item>

					<Form.Item
						name="videos"
						label="Number of Assets"
						rules={[{ type: "number", min: 0, message: "Must be a non-negative number" }]}
					>
						<InputNumber min={0} placeholder="Enter number of assets" />
					</Form.Item>
				</Form>
			</DynamicModal>
		</>
	);
};
